import {
  Model,
  surveyLocalization,
  SurveyModel,
  FunctionFactory,
} from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { observer } from 'mobx-react';
import { PlainLightPanelless } from 'survey-core/themes';
import {
  IAnswer,
  IQuestionnaireAnswerPayload,
} from '../../global/entities/QuestionnaireAnswer/QuestionnaireAnswer.ts';
import { useCallback, useEffect, useMemo } from 'react';
import { useGetQuestionnaireAnswerByTaskId } from '../../global/entities/QuestionnaireAnswer/queries/useGetQuestionnaireAnswerByTaskId.tsx';
import { useGetQuestionnaireAnswerByTaskIdAndToken } from '../../global/entities/QuestionnaireAnswer/queries/useGetQuestionnaireAnswerByTaskIdAndToken.tsx';
import 'survey-core/i18n/french';
import { useTranslation } from 'react-i18next';
import { Placeholder } from '../../global/component/molecules/Placeholder.tsx';
import { t } from 'i18next';
import { IconCheck } from '@tabler/icons-react';
import { patientStore } from '../../global/Store/Store.ts';
import Utils from '../../global/helpers/utils.ts';

surveyLocalization.locales['fr'].pageNextText = 'Suivant';
surveyLocalization.locales['fr'].pagePrevText = 'Précédent';
surveyLocalization.locales['fr'].completeText = 'Terminer';

surveyLocalization.supportedLocales = ['en', 'fr'];

const SURVEY_DRAFT_PREFIX = 'survey-draft-';

function getSurveyStorageKey(
  taskId?: string,
  invitationToken?: string,
): string | null {
  if (taskId && taskId !== '0') return `${SURVEY_DRAFT_PREFIX}${taskId}`;
  if (invitationToken) return `${SURVEY_DRAFT_PREFIX}token-${invitationToken}`;
  return null;
}

// Register custom functions for SurveyJS expressions (not built-in)
FunctionFactory.Instance.register('round', function (params: any[]) {
  if (params.length < 1) return null;
  const value = params[0];
  const decimals = params.length > 1 ? params[1] : 0;
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
});

FunctionFactory.Instance.register('isEmpty', function (params: any[]) {
  if (params.length < 1) return true;
  const value = params[0];
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
});

export interface IQuestionnaireProps {
  taskId?: string;
  invitationToken?: string;
  handleSubmit: (data: IQuestionnaireAnswerPayload) => void;
}

interface ICalculatedValue {
  name: string;
  value: any;
}

const Questionnaire: React.FC<IQuestionnaireProps> = observer(
  ({ taskId, invitationToken, handleSubmit }) => {
    const { data: questionnaire } = invitationToken
      ? useGetQuestionnaireAnswerByTaskIdAndToken(taskId, invitationToken)
      : useGetQuestionnaireAnswerByTaskId();

    const { i18n } = useTranslation();

    const survey = useMemo(() => {
      if (!questionnaire?.questionnaireJson) return null;

      const model = new Model(questionnaire.questionnaireJson);
      let surveyTheme = PlainLightPanelless;
      if (questionnaire.themeJson) {
        try {
          surveyTheme = JSON.parse(questionnaire.themeJson);
        } catch (e) {
          console.error('Failed to parse theme JSON', e);
        }
      }
      model.applyTheme(surveyTheme);
      model.locale = i18n.language;

      // Populate default values if provided
      const defaultData: Record<string, any> = {};
      const patientName = patientStore.name;
      const patientDateOfBirth = patientStore.birthDate;

      const formattedDateOfBirth = Utils.formatDateToYYYYMMDD(patientDateOfBirth);

      if (patientName && model.getQuestionByName('fullname'))
        defaultData['fullname'] = patientName;

      if (formattedDateOfBirth && model.getQuestionByName('dob'))
        defaultData['dob'] = formattedDateOfBirth;

      // Set current date/datetime for all timestamp fields
      const currentDate = new Date();
      const localDateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const localTimeString = `${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}`;

      model.getAllQuestions().forEach((question) => {
        if (question.name.startsWith('timestamp_')) {
          const inputType = question.inputType;
          defaultData[question.name] =
            inputType === 'datetime-local'
              ? `${localDateString}T${localTimeString}` // yyyy-MM-ddThh:mm
              : localDateString; // yyyy-MM-dd
        }
      });

      // Set the default data on the survey model
      if (Object.keys(defaultData).length > 0) model.data = defaultData;

      // Restore saved draft from sessionStorage if available
      const storageKey = getSurveyStorageKey(taskId, invitationToken);
      if (storageKey) {
        try {
          const savedDraft = sessionStorage.getItem(storageKey);
          if (savedDraft) {
            const parsed = JSON.parse(savedDraft);
            if (parsed.data) 
              model.data = { ...model.data, ...parsed.data };
            
            if (typeof parsed.currentPageNo === 'number') 
              model.currentPageNo = parsed.currentPageNo;
            
          }
        } catch {
          sessionStorage.removeItem(storageKey);
        }
      }

      return model;
    }, [questionnaire, i18n.language]);

    const extractElements = useCallback((jsonData: { pages: any[] }) => {
      let allElements: any[] = [];

      if (jsonData?.pages && Array.isArray(jsonData.pages)) {
        jsonData.pages.forEach((page) => {
          if (page.elements && Array.isArray(page.elements))
            allElements = allElements.concat(page.elements);
        });
      }

      return allElements;
    }, []);

    const convertSurveyToPayload = useCallback(
      (survey: SurveyModel): IQuestionnaireAnswerPayload => {
        const surveyData = survey.data;
        const answers: IAnswer[] = [];
        const allElements = extractElements(survey.json);

        // Extract calculatedValues from survey
        const calculatedValues: Record<string, any> = {};
        if (survey.calculatedValues && Array.isArray(survey.calculatedValues)) {
          survey.calculatedValues.forEach((cv: ICalculatedValue) => {
            calculatedValues[cv.name] = cv.value;
            console.log(cv.name, cv.value);
          });
        }

        for (const questionName in surveyData) {
          if (Object.prototype.hasOwnProperty.call(surveyData, questionName)) {
            const value = surveyData[questionName];
            if (typeof value === 'object' && value !== null) {
              for (const subQuestion in value) {
                if (Object.prototype.hasOwnProperty.call(value, subQuestion)) {
                  const subValue = value[subQuestion];
                  const questionElement = allElements.find(
                    (el) => el.name === questionName,
                  );
                  const isCommentType = questionElement?.type === 'comment';
                  answers.push({
                    questionName: subQuestion,
                    textAnswer:
                      typeof subValue === 'string'
                        ? subValue
                        : subValue.toString(),
                    note: isCommentType ? subValue : '',
                  });
                }
              }
            } else {
              answers.push({
                questionName: questionName,
                textAnswer:
                  typeof value === 'string' ? value : value.toString(),
                note: '',
              });
            }
          }
        }

        return {
          taskID: taskId !== '0' ? Number(taskId) : null,
          invitationToken: invitationToken || null,
          answersJson: JSON.stringify(surveyData),
          answers: answers,
          calculatedValues:
            Object.keys(calculatedValues).length > 0
              ? calculatedValues
              : undefined,
        };
      },
      [extractElements, taskId, invitationToken],
    );

    useEffect(() => {
      if (!survey) return;

      const onCompleteHandler = (sender: SurveyModel) => {
        const storageKey = getSurveyStorageKey(taskId, invitationToken);
        if (storageKey) sessionStorage.removeItem(storageKey);
        handleSubmit(convertSurveyToPayload(sender));
      };

      survey.onComplete.add(onCompleteHandler);

      // Cleanup: remove handler when survey changes/unmounts
      return () => {
        survey.onComplete.remove(onCompleteHandler);
      };
    }, [survey, handleSubmit, convertSurveyToPayload, taskId, invitationToken]);

    // Auto-save survey data to sessionStorage on value/page changes
    useEffect(() => {
      if (!survey) return;
      const storageKey = getSurveyStorageKey(taskId, invitationToken);
      if (!storageKey) return;

      const saveDraft = () => {
        try {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({
              data: survey.data,
              currentPageNo: survey.currentPageNo,
            }),
          );
        } catch {
          /* ignore quota errors */
        }
      };

      survey.onValueChanged.add(saveDraft);
      survey.onCurrentPageChanged.add(saveDraft);
      return () => {
        survey.onValueChanged.remove(saveDraft);
        survey.onCurrentPageChanged.remove(saveDraft);
      };
    }, [survey, taskId, invitationToken]);

    // Clear stale draft when questionnaire is already completed
    useEffect(() => {
      if (questionnaire?.isCompleted) {
        const storageKey = getSurveyStorageKey(taskId, invitationToken);
        if (storageKey) sessionStorage.removeItem(storageKey);
      }
    }, [questionnaire?.isCompleted, taskId, invitationToken]);

    if (!questionnaire?.questionnaireJson) return null;

    return questionnaire.isCompleted ? (
      <Placeholder
        icon={<IconCheck color={'green'} />}
        title={t('placeHolder.surveyIsCompleted')}
      />
    ) : (
      <SurveyComponent model={survey} />
    );
  },
);

export default Questionnaire;
